import React, { useEffect, useState } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Loader, ErrorBanner } from '../../components/common/Feedback';
import { useAuth } from '../../context/AuthContext';
import { fetchDashboard } from '../../api/dashboard';
import { formatCurrency, formatDateTime } from '../../utils/format';

const StatCard = ({ label, value }) => (
  <div className="stat-card">
    <div className="stat-label">{label}</div>
    <div className="stat-value">{value}</div>
  </div>
);

function SuperAdminStats({ data }) {
  return (
    <div className="card-row">
      <StatCard label="Total Clients" value={data.totalClients} />
      <StatCard label="Active Clients" value={data.activeClients} />
      <StatCard label="Total Employees" value={data.totalEmployees} />
      <StatCard label="Active Projects" value={data.activeProjects} />
      <StatCard label="Open Tickets" value={data.openTickets} />
      <StatCard label="Outstanding Invoices" value={formatCurrency(data.outstandingInvoiceAmount)} />
      <StatCard label="Pending Payment Verifications" value={data.pendingPaymentVerifications} />
    </div>
  );
}

function AdminStats({ data }) {
  return (
    <div className="card-row">
      <StatCard label="Active Projects" value={data.activeProjects} />
      <StatCard label="Pending Tasks" value={data.pendingTasks} />
      <StatCard label="Open Tickets" value={data.openTickets} />
      <StatCard label="Pending Document Requests" value={data.pendingDocumentRequests} />
      <StatCard label="Outstanding Invoices" value={formatCurrency(data.outstandingInvoiceAmount)} />
    </div>
  );
}

function EmployeeStats({ data }) {
  return (
    <div className="card-row">
      <StatCard label="My Open Tasks" value={data.myOpenTasks} />
      <StatCard label="Completed Tasks" value={data.myCompletedTasks} />
      <StatCard label="Tasks Due Today" value={data.tasksDueToday} />
      <StatCard label="My Open Tickets" value={data.myOpenTickets} />
    </div>
  );
}

function ClientStats({ data }) {
  return (
    <div className="card-row">
      <StatCard label="Active Projects" value={data.activeProjects} />
      <StatCard label="Pending Document Requests" value={data.pendingDocumentRequests} />
      <StatCard label="Open Invoices" value={data.openInvoices} />
      <StatCard label="Open Support Tickets" value={data.openTickets} />
    </div>
  );
}

export default function Dashboard() {
  const { actor } = useAuth();
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboard()
      .then((res) => setPayload(res))
      .catch((err) => setError(err.response?.data?.message || 'Failed to load dashboard.'));
  }, []);

  const displayName = actor?.profile?.name || actor?.profile?.clientName;

  return (
    <DashboardLayout title="Dashboard">
      <div className="page-header">
        <div>
          <h1 className="heading">Welcome, {displayName}</h1>
          <p className="page-subtext">Here is what's happening across the firm today.</p>
        </div>
      </div>

      <ErrorBanner message={error} />
      {!payload && !error && <Loader text="Loading dashboard..." />}

      {payload && (
        <>
          {payload.role === 'super_admin' && <SuperAdminStats data={payload.data} />}
          {payload.role === 'admin' && <AdminStats data={payload.data} />}
          {payload.role === 'employee' && <EmployeeStats data={payload.data} />}
          {payload.role === 'client' && <ClientStats data={payload.data} />}

          {Array.isArray(payload.data.recentAnnouncements) && payload.data.recentAnnouncements.length > 0 && (
            <div className="card" style={{ marginTop: 20 }}>
              <h3 className="subheading">Recent Announcements</h3>
              {payload.data.recentAnnouncements.map((a) => (
                <div key={a.id} style={{ marginBottom: 12 }}>
                  <strong>{a.title}</strong>
                  <div className="text-muted" style={{ fontSize: '9.5pt' }}>{formatDateTime(a.createdAt)}</div>
                  <p style={{ marginTop: 4 }}>{a.content}</p>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </DashboardLayout>
  );
}
